import React, { useState, useEffect } from 'react';
import { SearchDropdown, AuroraWorkflowModal } from '../../../../../components';
import { ConfirmationModal, ConfirmationType } from '../../../../../components/shared/ui/ConfirmationModal';
import { auroraService } from '../../../../../services';
import { AsanaTask, TaskPlatform } from '../../../../../types';
import { SEARCH_CONFIG, AURORA_PLATFORM_OPTIONS, DEFAULT_AURORA_PLATFORM, AuroraPlatform, HTTP_STATUS, TASK_PLATFORM_LABELS } from '../../../../../constants';
import { useTaskSearch } from '../../../../../hooks/useTaskSearch';
import { fetchAndSelectTask } from '../../../../../utils/task-lookup.utils';
import {
  buildSystemConfig,
  matchAllBomItems,
  buildPayloadFromReviewed,
  filterSystemConfig,
  extractStructuresRoofDetails,
  AuroraSystemConfig,
  BomMatchResults,
  ReviewedItemSelections,
  StructureRoofDetail,
  AuroraPanelPoolRow,
} from '../../../../../utils/aurora-mapper.utils';
import { CustomerInfoFields } from './CustomerInfoFields';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  setAuroraDesignId,
  setAuroraData,
  clearAuroraData,
  applyAuroraSystemConfig,
  setStructuresRoofsDetails,
  setAuroraPanelRowsPool,
  updateStructureData,
  setCurrentStep,
  addCompletedStep,
  WorkflowStep,
  SelectedTaskStub,
} from '../../../../../store/slices/createCRSlice';
import {
  selectAuroraDesignId,
  selectAuroraData,
  selectStructuresRoofsDetails,
  selectConfiguredSystems,
  selectAllSystemsModuleData,
} from '../../../../../store/selectors/createCRSelectors';
import {
  selectModuleBrands,
  selectSolarModules,
  selectInverterBrands,
  selectInverters,
  selectOptimizers,
} from '../../../../../store/selectors/databaseSelectors';

interface SimpleTaskSelectorProps {
  onTaskSelect: (task: AsanaTask | null) => void;
  selectedTask?: SelectedTaskStub;
  isTaskLocked?: boolean;
  onNext?: () => void;
}

export const SimpleTaskSelector: React.FC<SimpleTaskSelectorProps> = ({
  onTaskSelect,
  selectedTask,
  isTaskLocked = false,
}) => {
  const dispatch = useAppDispatch();
  const auroraDesignId = useAppSelector(selectAuroraDesignId);
  const auroraData = useAppSelector(selectAuroraData);
  const savedStructuresDetails = useAppSelector(selectStructuresRoofsDetails);
  const configuredSystems = useAppSelector(selectConfiguredSystems);
  const allSystemsModuleData = useAppSelector(selectAllSystemsModuleData);
  const moduleBrands = useAppSelector(selectModuleBrands);
  const allSolarModules = useAppSelector(selectSolarModules);
  const inverterBrands = useAppSelector(selectInverterBrands);
  const allInverters = useAppSelector(selectInverters);
  const allOptimizers = useAppSelector(selectOptimizers);

  // Method 1: search by customer name (results are fetched in full on select)
  const {
    searchQuery,
    searchResults,
    searchOptions,
    isSearching,
    isFetchingSelection,
    errorMessage: searchErrorMessage,
    handleSearch,
    handleSelect: handleSearchResultSelect,
    resetSearch,
  } = useTaskSearch(onTaskSelect);

  // Method 2: task ID direct lookup state (Asana / ClickUp)
  const [taskPlatform, setTaskPlatform] = useState<TaskPlatform>('asana');
  const [taskIdInput, setTaskIdInput] = useState('');
  const [isLoadingTask, setIsLoadingTask] = useState(false);
  const [taskFetchError, setTaskFetchError] = useState<string | null>(null);

  // Aurora state
  const [auroraInput, setAuroraInput] = useState(auroraDesignId || '');
  const [auroraPlatform, setAuroraPlatform] = useState<AuroraPlatform>(DEFAULT_AURORA_PLATFORM);
  const [isLoadingAurora, setIsLoadingAurora] = useState(false);
  const [auroraLoadingStatus, setAuroraLoadingStatus] = useState('');
  const [auroraError, setAuroraError] = useState<string | null>(null);

  // Aurora workflow modal state
  const [systemConfig, setSystemConfig] = useState<AuroraSystemConfig | null>(null);
  const [bomMatchResults, setBomMatchResults] = useState<BomMatchResults | null>(null);
  const [reviewedSelections, setReviewedSelections] = useState<ReviewedItemSelections | null>(null);
  const [structuresDetails, setStructuresDetails] = useState<StructureRoofDetail[]>([]);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  const [allStepsConfirmed, setAllStepsConfirmed] = useState(false);
  const [workflowKey, setWorkflowKey] = useState(0);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Sync local aurora input with redux state
  useEffect(() => {
    setAuroraInput(auroraDesignId || '');
  }, [auroraDesignId]);

  // Rebuild local workflow state from Redux after hydration (e.g. resume from Review CRs)
  useEffect(() => {
    if (!auroraData?.design_summary || bomMatchResults) return;
    const config = buildSystemConfig(auroraData.design_summary);
    if (!config || config.systemCount === 0) return;

    setSystemConfig(config);
    setBomMatchResults(matchAllBomItems(config, {
      moduleBrands,
      solarModules: allSolarModules,
      inverterBrands,
      inverters: allInverters,
      optimizers: allOptimizers,
    }));

    const structures = savedStructuresDetails ?? extractStructuresRoofDetails(
      auroraData.roof_design_summary ?? null,
      auroraData.design_summary ?? null,
    );
    setStructuresDetails(structures);
    setAllStepsConfirmed(true);
  }, [auroraData?.design_summary]);

  useEffect(() => {
    if (!selectedTask) {
      resetSearch();
    }
  }, [selectedTask, resetSearch]);

  const platformLabel = TASK_PLATFORM_LABELS[taskPlatform];
  // Only one lookup at a time: a slower fetch would otherwise overwrite the first selection.
  const isLookupBusy = isLoadingTask || isFetchingSelection;

  const handleFetchTask = async () => {
    const taskId = taskIdInput.trim();
    if (!taskId) {
      setTaskFetchError('Please enter a Task ID');
      return;
    }

    setIsLoadingTask(true);
    setTaskFetchError(null);

    try {
      const fetchError = await fetchAndSelectTask(taskPlatform, taskId, onTaskSelect);
      if (fetchError) {
        setTaskFetchError(fetchError);
      } else {
        setTaskIdInput('');
      }
    } finally {
      setIsLoadingTask(false);
    }
  };

  const handleAuroraInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAuroraInput(value);
    setAuroraError(null);

    // Clear aurora data if input is cleared
    if (!value.trim()) {
      dispatch(clearAuroraData());
    }
  };

  const parseAuroraDesignId = (input: string): string => {
    const trimmed = input.trim();
    const urlMatch = trimmed.match(/\/designs\/([a-f0-9-]+)/i);
    if (urlMatch) {
      return urlMatch[1];
    }
    return trimmed;
  };

  const handleFetchAuroraDesign = async () => {
    if (!auroraInput.trim()) {
      setAuroraError('Please enter a Design ID or URL');
      return;
    }

    const designId = parseAuroraDesignId(auroraInput);

    if (!designId) {
      setAuroraError('Could not extract a valid Design ID');
      return;
    }

    setIsLoadingAurora(true);
    setAuroraError(null);
    setAuroraLoadingStatus('Getting project design summary...');

    try {
      const response = await auroraService.getDesignById(designId, auroraPlatform);

      setAuroraLoadingStatus('Processing design data...');

      dispatch(setAuroraDesignId(designId));
      const structuresFromDesign = extractStructuresRoofDetails(
        response.roof_design_summary ?? null,
        response.design_summary ?? null,
      );

      dispatch(setAuroraData({
        design_summary: response.design_summary,
        roof_design_summary: response.roof_design_summary,
        structures_roofs_details: structuresFromDesign,
      }));
      setStructuresDetails(structuresFromDesign);

      // Build system config proposal and match BOM items
      const config = buildSystemConfig(response.design_summary);
      if (config && config.systemCount > 0) {
        setSystemConfig(config);
        const matches = matchAllBomItems(config, {
          moduleBrands,
          solarModules: allSolarModules,
          inverterBrands,
          inverters: allInverters,
          optimizers: allOptimizers,
        });
        setBomMatchResults(matches);
        setAllStepsConfirmed(false);
        setWorkflowKey(prev => prev + 1);
        setIsWorkflowModalOpen(true);
      }
    } catch (error: any) {
      console.error('Aurora fetch error:', error);

      let errorMessage = 'Failed to fetch Aurora design';

      if (error.message?.includes(HTTP_STATUS.NOT_FOUND) || error.message?.includes('not found')) {
        errorMessage = `Design with ID "${designId}" not found`;
      } else if (error.message?.includes(HTTP_STATUS.UNAUTHORIZED) || error.message?.includes(HTTP_STATUS.FORBIDDEN)) {
        errorMessage = 'Aurora API authentication failed';
      } else if (error.message?.includes(HTTP_STATUS.SERVICE_UNAVAILABLE) || error.message?.includes('unavailable')) {
        errorMessage = 'Aurora API is currently unavailable';
      }

      setAuroraError(errorMessage);
      dispatch(clearAuroraData());
    } finally {
      setIsLoadingAurora(false);
      setAuroraLoadingStatus('');
    }
  };

  const handleClearAurora = () => {
    setAuroraInput('');
    setAuroraError(null);
    dispatch(clearAuroraData());
    // Reset local workflow state
    setSystemConfig(null);
    setBomMatchResults(null);
    setReviewedSelections(null);
    setStructuresDetails([]);
    setAllStepsConfirmed(false);
  };

  // Workflow: Step 1 confirm → filter config for Step 2
  const handleReviewConfirm = (reviewed: ReviewedItemSelections, removedIds: Set<string>) => {
    setReviewedSelections(reviewed);
    if (systemConfig) {
      setSystemConfig(filterSystemConfig(systemConfig, removedIds));
    }
  };

  // Workflow: Step 2 confirm → apply payload to Redux, prepare structures for Step 3
  const handleMappingConfirm = (confirmedConfig: AuroraSystemConfig) => {
    if (reviewedSelections) {
      const payload = buildPayloadFromReviewed(confirmedConfig, reviewedSelections);
      dispatch(applyAuroraSystemConfig(payload));
    }
    const details = savedStructuresDetails?.length
      ? savedStructuresDetails
      : extractStructuresRoofDetails(
          auroraData?.roof_design_summary ?? null,
          auroraData?.design_summary ?? null,
        );
    setStructuresDetails(details);
    dispatch(setStructuresRoofsDetails(details));
  };

  // Workflow: Step 3 confirm → persist structures + panel rows pool to Redux, pre-populate Mounting structures, close modal, navigate to Equipment
  const handleWorkflowComplete = (details: StructureRoofDetail[], pool: AuroraPanelPoolRow[]) => {
    dispatch(setStructuresRoofsDetails(details));
    setStructuresDetails(details);
    dispatch(setAuroraPanelRowsPool(pool));

    // Pre-populate Mounting step structures with Aurora structure names
    details.forEach((detail, idx) => {
      if (idx < 3) {
        dispatch(updateStructureData({
          structureNumber: (idx + 1) as 1 | 2 | 3,
          data: { name: detail.structure_name },
        }));
      }
    });

    setAllStepsConfirmed(true);
    setIsWorkflowModalOpen(false);
    dispatch(addCompletedStep(WorkflowStep.TASK));
    dispatch(setCurrentStep(WorkflowStep.EQUIPMENT));
  };

  const handleCloseWorkflowModal = () => {
    setIsWorkflowModalOpen(false);
  };

  // Preview: reopen workflow — only show steps as confirmed if workflow was fully completed
  const handleOpenPreview = () => {
    const existing = savedStructuresDetails;
    if (existing && existing.length > 0) {
      setStructuresDetails(existing);
    } else {
      const details = extractStructuresRoofDetails(
        auroraData?.roof_design_summary ?? null,
        auroraData?.design_summary ?? null,
      );
      setStructuresDetails(details);
      dispatch(setStructuresRoofsDetails(details));
    }
    setWorkflowKey(prev => prev + 1);
    setIsWorkflowModalOpen(true);
  };

  return (
    <div className="space-y-6 3xl:space-y-3.5">
      {selectedTask ? (
        /* Selected task + Customer Info — unified card */
        <div className="rounded-xl 3xl:rounded-2xl overflow-hidden transition-all duration-200 bg-white border border-slate-200/80 border-l-[3px] border-l-emerald-400 shadow-md">
          {/* Selected task header */}
          <div className="px-5 py-3 3xl:px-6 3xl:py-3.5 border-b border-slate-200/60">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm shadow-emerald-500/20">
                  <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <span className="text-sm 3xl:text-base font-semibold text-slate-900 truncate block leading-snug">{selectedTask.name}</span>
                  <span className="text-[10px] 3xl:text-[11px] font-mono text-slate-400 tracking-wide">{selectedTask.gid}</span>
                </div>
              </div>
              {isTaskLocked ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] 3xl:text-[11px] font-medium text-slate-400 bg-slate-50 border border-slate-200">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Locked
                </span>
              ) : (
                <button
                  onClick={() => onTaskSelect(null)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] 3xl:text-[11px] font-medium text-red-500 bg-red-50 border border-red-200 hover:text-red-700 hover:bg-red-100 transition-all duration-200"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Change
                </button>
              )}
            </div>
          </div>

          {/* Customer Info Fields */}
          <CustomerInfoFields disabled={false} />
        </div>
      ) : (
        /* Landing state — hero + input methods */
        <div className="rounded-xl 3xl:rounded-2xl overflow-hidden bg-white border border-slate-200/80 shadow-sm">
          {/* Hero section */}
          <div className="px-8 pt-8 pb-5 3xl:px-10 3xl:pt-10 3xl:pb-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 3xl:w-16 3xl:h-16 bg-gradient-to-br from-indigo-500 to-sky-500 rounded-2xl shadow-lg shadow-indigo-500/20 mb-4">
              <svg className="w-7 h-7 3xl:w-8 3xl:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h2 className="text-xl 3xl:text-2xl font-bold text-slate-900 mb-2">Find Your Job</h2>
            <p className="text-sm 3xl:text-base text-slate-500 max-w-lg mx-auto leading-relaxed">
              Search Asana and ClickUp jobs by customer name, or fetch task details directly from Asana or ClickUp using a task ID.
            </p>
          </div>

          {/* Divider */}
          <div className="mx-8 3xl:mx-10 border-t border-slate-200" />

          {/* Input methods */}
          <div className="px-8 py-5 3xl:px-10 3xl:py-6 space-y-4">
            {/* Method 1: Search */}
            <div>
              <label className="block text-[10px] 3xl:text-[11px] font-semibold text-orange-500 uppercase tracking-wider mb-2">Search by Customer Name</label>
              <SearchDropdown
                options={searchQuery.length >= SEARCH_CONFIG.MIN_SEARCH_LENGTH ? searchOptions : []}
                onSelect={handleSearchResultSelect}
                onSearch={handleSearch}
                placeholder="Type customer full name (min. 3 characters)..."
                isLoading={isSearching || isFetchingSelection}
                disabled={isLookupBusy}
                error={searchErrorMessage || undefined}
                className="text-xs 3xl:text-sm"
                inputClassName="w-full text-left px-3 py-2 2xl:px-3 2xl:py-2.5 3xl:px-3.5 3xl:py-3 border border-slate-300 bg-white rounded-lg text-[11px] 2xl:text-xs 3xl:text-sm text-slate-900 placeholder-slate-400 placeholder:text-[10px] 2xl:placeholder:text-[11px] 3xl:placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                enableArrowNavigation={true}
                isTaskDropdown={true}
              />
              {searchQuery.length >= SEARCH_CONFIG.MIN_SEARCH_LENGTH && searchResults.length === 0 && !isSearching && !searchErrorMessage && (
                <div className="mt-1.5 text-[10px] 3xl:text-[11px] text-slate-500 font-medium">No matching tasks found</div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-slate-300" />
              <span className="text-[10px] 3xl:text-[11px] font-semibold text-orange-500 uppercase tracking-wider">or fetch by task ID</span>
              <div className="flex-1 border-t border-slate-300" />
            </div>

            {/* Method 2: Direct task ID */}
            <div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5 p-0.5 rounded-lg border border-slate-300 bg-white shadow-sm flex-shrink-0">
                  {(['asana', 'clickup'] as const).map((platform) => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => { setTaskPlatform(platform); setTaskFetchError(null); }}
                      disabled={isLookupBusy}
                      className={`px-3.5 py-1.5 2xl:py-2 3xl:py-2.5 rounded-md text-[10px] 2xl:text-[11px] 3xl:text-xs font-bold tracking-wide transition-all duration-200 ${
                        taskPlatform === platform
                          ? 'bg-orange-500 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-100'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {TASK_PLATFORM_LABELS[platform]}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={taskIdInput}
                  onChange={e => { setTaskIdInput(e.target.value); setTaskFetchError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter' && taskIdInput.trim()) handleFetchTask(); }}
                  placeholder={`Enter ${platformLabel} Task ID...`}
                  disabled={isLookupBusy}
                  className={`flex-1 px-3 py-2 2xl:px-3 2xl:py-2.5 3xl:px-3.5 3xl:py-3 border bg-white rounded-lg text-[11px] 2xl:text-xs 3xl:text-sm text-slate-900 placeholder-slate-400 placeholder:text-[10px] 2xl:placeholder:text-[11px] 3xl:placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 ${taskFetchError ? 'border-red-300 bg-red-50/30' : 'border-slate-300'}`}
                />
                <button
                  onClick={handleFetchTask}
                  disabled={isLookupBusy || !taskIdInput.trim()}
                  className="px-4 py-2 2xl:px-4.5 2xl:py-2.5 3xl:px-5 3xl:py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed disabled:shadow-none text-white font-bold rounded-lg shadow-sm shadow-orange-500/20 hover:shadow-md hover:shadow-orange-500/25 transition-all duration-200 flex items-center gap-1.5 text-[11px] 2xl:text-xs 3xl:text-sm flex-shrink-0"
                >
                  {isLoadingTask ? (
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Fetch
                    </>
                  )}
                </button>
              </div>
              {taskFetchError && (
                <div className="mt-1.5 text-[10px] 3xl:text-[11px] text-red-600 font-medium flex items-center gap-1.5">
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {taskFetchError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aurora Design section — only when task selected */}
      {selectedTask && (
      <div className={`rounded-xl 3xl:rounded-2xl overflow-hidden transition-all duration-200 bg-white border border-slate-200/80 border-l-[3px] border-l-emerald-400 ${
        auroraData?.design_summary ? 'shadow-md' : 'shadow-sm hover:shadow-md'
      }`}>
        {/* Card Header */}
        <div className={`flex items-center justify-between px-5 py-3 3xl:px-6 3xl:py-3.5 border-b ${
          auroraData?.design_summary
            ? 'bg-gradient-to-r from-emerald-50 to-green-50/80 border-emerald-200/60'
            : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Aurora Design</span>
            {auroraData?.design_summary && (
              <span className="text-[9px] 3xl:text-[10px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200/50">{auroraPlatform.toUpperCase()}</span>
            )}
          </div>
          {auroraData?.design_summary && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleOpenPreview}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] 3xl:text-[11px] font-semibold text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 transition-all duration-200 shadow-sm"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </button>
              <button
                onClick={() => setShowRemoveConfirm(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] 3xl:text-[11px] font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 transition-all duration-200 shadow-sm"
                title="Remove design"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove
              </button>
            </div>
          )}
        </div>

        {/* Card Content */}
        <div className="px-5 py-4 3xl:px-6 3xl:py-5">
          {auroraData?.design_summary ? (
            <div className="flex items-center gap-3 px-3.5 py-2.5 bg-emerald-50/50 border border-emerald-100 rounded-lg">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[11px] 3xl:text-xs font-semibold text-emerald-700">Design Loaded</span>
                <code className="block text-[9px] 3xl:text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 w-fit font-mono truncate max-w-full">{auroraDesignId}</code>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-0.5 p-0.5 rounded-lg border border-slate-300 bg-white shadow-sm flex-shrink-0">
                  {AURORA_PLATFORM_OPTIONS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAuroraPlatform(key)}
                      disabled={isLoadingAurora}
                      className={`px-3.5 py-1.5 2xl:py-2 3xl:py-2.5 rounded-md text-[10px] 2xl:text-[11px] 3xl:text-xs font-bold tracking-wide transition-all duration-200 ${
                        auroraPlatform === key
                          ? 'bg-emerald-500 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-100'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={auroraInput}
                  onChange={handleAuroraInputChange}
                  placeholder="Paste Aurora URL or Design ID..."
                  disabled={isLoadingAurora}
                  className={`flex-1 w-full px-3 py-2 border rounded-lg text-[11px] 2xl:text-xs 3xl:text-sm text-slate-900 placeholder:text-[10px] 2xl:placeholder:text-[11px] 3xl:placeholder:text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all duration-200 ${auroraError ? 'border-red-300 bg-red-50/30' : 'border-slate-300 bg-white'}`}
                />
                <button
                  onClick={handleFetchAuroraDesign}
                  disabled={isLoadingAurora || !auroraInput.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-sm shadow-emerald-500/20 transition-all duration-200 flex items-center gap-1.5 text-[11px] 2xl:text-xs 3xl:text-sm flex-shrink-0"
                >
                  {isLoadingAurora ? (
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Fetch Design
                    </>
                  )}
                </button>
              </div>

              {isLoadingAurora && auroraLoadingStatus && (
                <div className="text-[10px] 3xl:text-[11px] text-emerald-600 flex items-center gap-2 bg-emerald-50/50 rounded-lg px-3 py-1.5 border border-emerald-100/50">
                  <div className="w-3 h-3 border-[1.5px] border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                  <span className="font-medium">{auroraLoadingStatus}</span>
                </div>
              )}

              {auroraError && (
                <div className="text-[10px] 3xl:text-[11px] text-red-600 bg-red-50/80 rounded-lg px-3 py-2 border border-red-200/60 flex items-start gap-2">
                  <svg className="w-3 h-3 flex-shrink-0 mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">{auroraError}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {bomMatchResults && systemConfig && (
        <AuroraWorkflowModal
          key={workflowKey}
          isOpen={isWorkflowModalOpen}
          onClose={handleCloseWorkflowModal}
          bomMatchResults={bomMatchResults}
          moduleBrands={moduleBrands}
          solarModules={allSolarModules}
          inverterBrands={inverterBrands}
          inverters={allInverters}
          optimizers={allOptimizers}
          onReviewConfirm={handleReviewConfirm}
          systemConfig={systemConfig}
          onMappingConfirm={handleMappingConfirm}
          initialStructures={structuresDetails}
          onStructuresChange={(details: StructureRoofDetail[]) => dispatch(setStructuresRoofsDetails(details))}
          designSummary={auroraData?.design_summary ?? null}
          onPoolExtracted={(pool: AuroraPanelPoolRow[]) => dispatch(setAuroraPanelRowsPool(pool))}
          onComplete={handleWorkflowComplete}
          allConfirmed={allStepsConfirmed}
          initialPool={auroraData?.aurora_panel_rows_pool ?? undefined}
        />
      )}

      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={() => { setShowRemoveConfirm(false); handleClearAurora(); }}
        title="Remove Aurora Design?"
        message="This will remove the Aurora design and clear all associated equipment, structure data, and panel row assignments. This action cannot be undone."
        type={ConfirmationType.DANGER}
        confirmButtonText="Remove"
        cancelButtonText="Keep"
      />
    </div>
  );
};
